import React, { useState, useEffect, useRef, useCallback } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import { Search, Users, FileText, Receipt, Wrench, X, ArrowRight, Loader2, AlertTriangle } from 'lucide-react'
import { formatNumarDoc } from '@edrikom/shared'
import { trpc } from '../../api/index.js'
import { useNav } from '../../stores/navigationStore'
import { cn } from '../../lib/utils'
import { useFocusTrap } from '../../lib/focusTrap.js'

interface GlobalSearchProps {
  onClose: () => void
}

type ResultItem =
  | { _type: 'client';     id: string; tip?: string; nume?: string | null; prenume?: string | null; denumireFirma?: string | null }
  | { _type: 'contract';   id: string; numarContract?: string | null; status?: string | null; clientId?: string | null }
  | { _type: 'factura';    id: string; serie?: string | null; numar?: number | null; status?: string | null; clientId?: string | null; anNumerotare?: number | null; dataFactura?: number | string | null; tip?: string | null }
  | { _type: 'echipament'; id: string; codIntern?: string | null; denumire?: string | null; status?: string | null }

/** Etichetele de status în română — rezultatele afișau valorile brute din DB („in_mentenanta", „emisa"). */
const STATUS_RO: Record<ResultItem['_type'], Record<string, string>> = {
  client: {},
  contract: {
    draft: 'Draft', activ: 'Activ', suspendat: 'Suspendat', litigiu: 'Litigiu', returnat: 'Returnat',
    finalizat: 'Finalizat', inchis: 'Închis', anulat: 'Anulat', reziliat: 'Reziliat',
  },
  factura: { draft: 'Ciornă', emisa: 'Emisă', platita: 'Plătită', stornata: 'Stornată' },
  echipament: {
    disponibil: 'Disponibil', inchiriat: 'Închiriat', rezervat: 'Rezervat', in_mentenanta: 'În mentenanță',
    defect: 'Defect', retras: 'Retras', casat: 'Casat',
  },
}

const TIP_FACTURA_RO: Record<string, string> = { factura: 'Factură', proforma: 'Proformă', nota_credit: 'Notă de credit' }

function itemLabel(item: ResultItem): string {
  switch (item._type) {
    case 'client':
      return item.tip === 'PJ'
        ? (item.denumireFirma ?? 'Firmă necunoscută')
        : [item.prenume, item.nume].filter(Boolean).join(' ') || 'Client necunoscut'
    case 'contract':
      return item.numarContract ?? `Contract ${item.id.slice(0, 8)}`
    case 'factura': {
      if (!item.serie || item.numar == null) return `Factură ${item.id.slice(0, 8)}`
      // Același format canonic ca pe PDF, în e-Factura și în lista de facturi.
      const an = item.anNumerotare ?? (item.dataFactura ? new Date(Number(item.dataFactura)).getFullYear() : '')
      return an ? formatNumarDoc(item.serie, item.numar, an) : `${item.serie}-${String(item.numar).padStart(4, '0')}`
    }
    case 'echipament':
      return [item.codIntern, item.denumire].filter(Boolean).join(' — ') || 'Echipament'
  }
}

function itemSub(item: ResultItem): string {
  const status = item._type !== 'client' && item.status ? (STATUS_RO[item._type][item.status] ?? item.status) : null
  switch (item._type) {
    case 'client':     return item.tip === 'PJ' ? 'Persoană juridică' : 'Persoană fizică'
    case 'contract':   return status ?? 'Contract'
    case 'factura': {
      const tip = TIP_FACTURA_RO[item.tip ?? 'factura'] ?? 'Factură'
      return status ? `${tip} · ${status}` : tip
    }
    case 'echipament': return status ?? 'Echipament'
  }
}

const TYPE_ICON = {
  client:     Users,
  contract:   FileText,
  factura:    Receipt,
  echipament: Wrench,
} as const

const TYPE_COLOR = {
  client:     'text-blue-400',
  contract:   'text-green-400',
  factura:    'text-purple-400',
  echipament: 'text-orange-400',
} as const

const TYPE_LABEL = {
  client:     'Clienți',
  contract:   'Contracte',
  factura:    'Facturi',
  echipament: 'Echipamente',
} as const

/** Valoare întârziată: nu lansăm o interogare (4 tabele, ILIKE) la fiecare tastă. */
function useDebounced<T>(valoare: T, ms: number): T {
  const [v, setV] = useState(valoare)
  useEffect(() => {
    const t = setTimeout(() => setV(valoare), ms)
    return () => clearTimeout(t)
  }, [valoare, ms])
  return v
}

export function GlobalSearch({ onClose }: GlobalSearchProps) {
  const [q, setQ] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const { navigate } = useNav()
  const [cursor, setCursor] = useState(0)
  useFocusTrap(dialogRef, true)

  const termen = useDebounced(q.trim(), 250)
  const activ = termen.length >= 2

  const searchQ = trpc.search.global.useQuery(
    { q: termen, limit: 5 },
    // Păstrăm rezultatele anterioare cât se încarcă următoarele — altfel lista „clipea"
    // (gol → rezultate) la fiecare literă tastată.
    { enabled: activ, placeholderData: keepPreviousData, staleTime: 15_000 },
  )

  const data = activ ? searchQ.data : undefined
  const allResults: ResultItem[] = data
    ? [
        ...data.clienti,
        ...data.contracte,
        ...data.facturi,
        ...data.echipamente,
      ]
    : []

  useEffect(() => { setCursor(0) }, [termen])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const select = useCallback((item: ResultItem) => {
    switch (item._type) {
      case 'client':
        navigate('clienti', { clientId: item.id })
        break
      case 'contract':
        navigate('contracte', { contractId: item.id })
        break
      case 'factura':
        navigate('facturare', { facturaId: item.id })
        break
      case 'echipament':
        navigate('echipamente', { echipamentId: item.id })
        break
    }
    onClose()
  }, [navigate, onClose])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, allResults.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)) }
    // Rezultatele afișate pot fi încă cele pentru textul VECHI (debounce 250 ms + keepPreviousData):
    // Enter ar fi deschis alt client/altă factură decât cea căutată. Așteptăm rezultatele noi.
    if (e.key === 'Enter' && allResults[cursor] && !rezultateInvechite) select(allResults[cursor])
    if (e.key === 'Escape') onClose()
  }

  const hasResults = allResults.length > 0
  const rezultateInvechite = (q.trim().length >= 2 && q.trim() !== termen) || searchQ.isPlaceholderData
  const asteaptaTastare = q.trim().length >= 2 && q.trim() !== termen
  const isLoading  = q.trim().length >= 2 && (asteaptaTastare || searchQ.isFetching)
  const eroare     = activ && searchQ.isError && !asteaptaTastare
  const optiuneActiva = hasResults ? `cautare-rezultat-${cursor}` : undefined

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-[15vh] px-3 sm:px-4 bg-black/60 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Căutare globală"
        className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-800">
          {isLoading
            ? <Loader2 size={16} className="text-slate-400 animate-spin shrink-0" aria-hidden />
            : <Search size={16} className="text-slate-400 shrink-0" aria-hidden />
          }
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Caută client, contract, factură, echipament..."
            aria-label="Termen de căutare"
            role="combobox"
            aria-expanded={hasResults}
            aria-controls="cautare-rezultate"
            aria-activedescendant={optiuneActiva}
            aria-autocomplete="list"
            className="flex-1 min-w-0 bg-transparent text-white text-base sm:text-sm placeholder-slate-500 focus:outline-none"
          />
          {q && (
            <button onClick={() => setQ('')} className="text-slate-500 hover:text-white" aria-label="Golește căutarea">
              <X size={14} />
            </button>
          )}
          <button onClick={onClose} className="sm:hidden text-xs text-slate-400 hover:text-white px-1" aria-label="Închide căutarea">
            Închide
          </button>
          <kbd className="hidden sm:inline text-[10px] text-slate-500 px-1.5 py-0.5 border border-slate-700 rounded font-mono">Esc</kbd>
        </div>

        {/* Results */}
        {q.trim().length >= 2 && (
          <div id="cautare-rezultate" role="listbox" aria-label="Rezultate" className="max-h-[60vh] sm:max-h-80 overflow-y-auto">
            {eroare && (
              <div role="alert" className="px-4 py-6 flex flex-col items-center gap-2 text-sm text-red-400">
                <span className="flex items-center gap-2"><AlertTriangle size={14} /> Căutarea nu a putut fi efectuată.</span>
                <button onClick={() => searchQ.refetch()} className="text-xs underline hover:text-red-300">Reîncearcă</button>
              </div>
            )}

            {!eroare && !hasResults && !isLoading && (
              <div className="px-4 py-8 text-center text-sm text-slate-500" role="status">
                Niciun rezultat pentru „{q.trim()}"
              </div>
            )}

            {!eroare && data && (
              <>
                {(['clienti', 'contracte', 'facturi', 'echipamente'] as const).map(group => {
                  const items = data[group] as ResultItem[]
                  if (!items.length) return null
                  const typeKey = items[0]._type
                  const Icon = TYPE_ICON[typeKey]
                  return (
                    <div key={group} role="group" aria-label={TYPE_LABEL[typeKey]}>
                      <div className="px-4 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider bg-slate-950/40">
                        {TYPE_LABEL[typeKey]}
                      </div>
                      {items.map(item => {
                        const globalIdx = allResults.indexOf(item)
                        const isActive = cursor === globalIdx
                        return (
                          <button
                            key={item.id}
                            id={`cautare-rezultat-${globalIdx}`}
                            role="option"
                            aria-selected={isActive}
                            onClick={() => select(item)}
                            onMouseEnter={() => setCursor(globalIdx)}
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                              isActive ? 'bg-slate-800' : 'hover:bg-slate-800/50',
                            )}
                          >
                            <Icon size={14} className={cn('shrink-0', TYPE_COLOR[typeKey])} aria-hidden />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{itemLabel(item)}</p>
                              <p className="text-xs text-slate-500">{itemSub(item)}</p>
                            </div>
                            {isActive && <ArrowRight size={13} className="text-slate-500 shrink-0" aria-hidden />}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}

        {/* Hints */}
        {q.trim().length < 2 && (
          <div className="px-4 py-5 space-y-3">
            <p className="text-xs text-slate-600 uppercase tracking-wider font-semibold">Navigare rapidă</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
              {([
                [Users,     'Clienți',     'clienti'],
                [FileText,  'Contracte',   'contracte'],
                [Receipt,   'Facturare',   'facturare'],
                [Wrench,    'Echipamente', 'echipamente'],
              ] as const).map(([Icon, label, page]) => (
                <button key={page} onClick={() => { navigate(page); onClose() }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800 hover:text-slate-300 transition-colors text-left">
                  <Icon size={13} aria-hidden /> {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="hidden sm:flex px-4 py-2 border-t border-slate-800 gap-4 text-[10px] text-slate-600">
          <span><kbd className="font-mono">↑↓</kbd> navighează</span>
          <span><kbd className="font-mono">Enter</kbd> deschide</span>
          <span><kbd className="font-mono">Esc</kbd> închide</span>
        </div>
      </div>
    </div>
  )
}
